import {
  BackEndValueType,
  BlockStyle,
  CommonHelperFunctions,
  ComponentType,
  EAppSupportType,
  EColorPickerTab,
  EDatasourceType,
  IAbsolutePosition,
  IActionDefinitionDto,
  IActionParameter,
  IAuthorizationDto,
  IComponent,
  IComponentChild,
  IComponentDefinition,
  IComponentSet,
  IDisplayItem,
  IFixedPosition,
  IGenericSelectDataModel,
  IProject,
  IProjectAccess,
  IProjectDto,
  IProjectUserRole,
  IProjectMeta,
  IProperty,
  IPropertyTypeOptions,
  IScreenCanvasSize,
  getReportCanvasSize,
  IScreenDesign,
  IScreenDesignDictionary,
  IScreenDesignDto,
  IScreenDesignInfo,
  IScreenDesignInfoDto,
  IScreenDesignMeta,
  IScreenDesignPublicJSON,
  ISearchServiceSettings,
  ISymbol,
  IUser,
  IUserInfoDto,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import htmlComponentsJson from "@kuika/kuika-cl-model/json/html-components.json";
import { EManagedDbUpdateWhereType, ESymbolCategory } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import dayjs from "dayjs";
import { Guid } from "guid-typescript";
import * as _ from "lodash";
import LocalStorageService from "../../auth/local-storage-service";
import { toastService } from "../../components/ui-kit/kuika-toast/toast-service";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { IAutosaveDbDto } from "../../models/indexeddb-objects/autosave-db.dto";
import { normalizePreviewUrl } from "../../services/preview-url-service";
import confJson from "../data/conf.json";
import customersJson from "../data/customers.json";
import multiLanguageJson from "../data/multi-language.json";
import { IDuplicateOffset, IOccupiedOrigin, resolveDuplicateOffset } from "./duplicate-placement";
import { ScreenDesignHelper } from "./screen-design-helper";

const multiLanguage = multiLanguageJson;
const domainIds = customersJson.ids;
const { kuikaDevelopers } = confJson;

// Compile-time Main AI (Havelsan white-label) toggle. Must be flipped in lockstep with the
// backend `PLATFORM_DOMAIN=havelsan.com.tr` rollout: the designer keys branding off this constant,
// while the generated-app favicon (ReactProjectGenerator.cs) keys off that env var.
const isMainAI = false;

declare let window: any;
export interface IExcelRowToComponentResult {
  errorFound: boolean;
  errorString: string[];
  component: IComponent;
}

interface IApiErrorData {
  message?: string;
}

interface IApiErrorResponseBody {
  data?: IApiErrorData;
}

interface IApiErrorLike {
  response?: IApiErrorResponseBody;
}

export class KMainFunctions {
  static htmlComponentList: IComponent[] = htmlComponentsJson as unknown as IComponent[];

  static excelTitles = [
    "name",
    "tagName",
    "isItChildItem",
    "hasValidate",
    "isItNavigationComponent",
    "isItHiddenFromComponentList",
    "appSupportType",
    "isDeprecated",
    "isRenamingDisabled",
    "wrapWithFormItem",
    "blockStyle",
    "componentType",
    "addChildText",
    "removeChildText",
    "dropAreaText",
    "properties",
    "childrenInfo",
    "icon",
    "titleAttributeName"
  ];

  static exportFile(str: string, fileName: string) {
    const element = document.createElement("a");
    element.setAttribute("href", `data:text/plain;charset=utf-8,${encodeURIComponent(str)}`);
    element.setAttribute("download", fileName);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  static filterArray(array, keywords) {
    let filteredArray = [] as any[];
    if (array && array.length > 0) {
      let searchPattern: any = "";
      if (keywords) {
        searchPattern = new RegExp(keywords.map((term: any) => (term ? `(?=.*${term})` : "")).join(""), "i");
      }
      filteredArray = array.filter((c) => {
        return c?.name.match(searchPattern);
      });
    } else {
      filteredArray = array;
    }
    return filteredArray;
  }

  static isObjectEmpty(obj: any) {
    if (!obj) return true;
    if (Object.keys(obj).length === 0) return true;
    return false;
  }

  /**
   * Legacy display-item ids were minted in [1, 1_000_000]; new ids start above that
   * population and stay within int32 because backend/codegen consumers may parse
   * display-item ids as C# int.
   */
  private static readonly DISPLAY_ITEM_ID_MIN: number = 1_000_001;
  private static readonly DISPLAY_ITEM_ID_SPAN: number = 2_147_483_647 - KMainFunctions.DISPLAY_ITEM_ID_MIN + 1;

  static generateDisplayItemId(screenDesignDictionary: IScreenDesignDictionary = {}): number {
    // Crypto-random over ~2^31 values instead of Math.random over 10^6: display items are
    // also minted on other clients (multi-user editing, AI generation), and the dictionary
    // passed here only knows about LOCAL ids — the wide space is what keeps two clients
    // from minting the same id for different nodes.
    const buffer: Uint32Array<ArrayBuffer> = new Uint32Array(1);
    crypto.getRandomValues(buffer);
    const id: number = KMainFunctions.DISPLAY_ITEM_ID_MIN + (buffer[0] % KMainFunctions.DISPLAY_ITEM_ID_SPAN);

    if (screenDesignDictionary[id]) {
      return this.generateDisplayItemId(screenDesignDictionary);
    }

    return id;
  }

  static cloneDeepDisplayItemsWithChildren(
    diList: IDisplayItem[],
    parentId: number,
    dict: IScreenDesignDictionary
  ): IDisplayItem[] {
    const newDiList: IDisplayItem[] = [];
    for (let index = 0; index < diList.length; index++) {
      newDiList[index] = KMainFunctions.updateCloneChildDi(
        _.cloneDeep(diList[index]),
        parentId,
        diList,
        undefined,
        dict
      );
    }
    return newDiList;
  }

  static findAllComponentsWithSameTag(di: IDisplayItem, displayItems: IDisplayItem[]) {
    let count = 0;
    displayItems.forEach((child) => {
      const item1 = di.item;
      const item2 = child.item;

      if (item1.name == item2.name) {
        count += 1;
      }
      count += this.findAllComponentsWithSameTag(di, child.children);
    });
    return count;
  }

  static findDiByFriendlyName(friendlyName: string, displayItems: IDisplayItem[]): boolean {
    const isFriendNameExist = ScreenDesignHelper.findDisplayItemByFriendlyName(friendlyName, displayItems);
    if (isFriendNameExist) {
      return true;
    }
    return false;
  }

  /**
   * `pageWidth` is the report page's width in the agent's CSS px, or null when the design has no page
   * (the copy is then not kept inside one). Not the iframe's width: in the report designer the frame is
   * the page divided by the zoom.
   */
  static cloneDisplayItemWithChildren(
    sd: IScreenDesign,
    dict: IScreenDesignDictionary,
    di: IDisplayItem,
    pageWidth: number | null
  ) {
    const friendlyNames = new Set<string>();
    ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems, friendlyNames);

    let newDisplayItem: IDisplayItem = {
      ...di,
      item: {
        ...di.item,
        events: [],
        properties: _.cloneDeep(di.item.properties)
      },
      children: [],
      id: ScreenDesignHelper.generateUniqueDisplayItemId(dict)
    };

    let diJson = JSON.stringify(newDisplayItem);
    diJson = ScreenDesignHelper.replaceAnIdInSerializedDi(diJson, di.id, newDisplayItem.id);
    newDisplayItem = JSON.parse(diJson);

    newDisplayItem.friendlyName = ScreenDesignHelper.generateUniqueFriendlyName(
      newDisplayItem.friendlyName || newDisplayItem.item.name || "Component",
      friendlyNames
    );

    if (di?.fixedPositionInfo && sd?.isAbsoluteDesign === true) {
      const parentDi = di.parentId ? dict[di.parentId] : undefined;
      let fixedPositionInfo = _.cloneDeep(di.fixedPositionInfo);
      let absolutePositionInfo = _.cloneDeep(di.absolutePositionInfo);

      if (parentDi) {
        const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
        const canvasWidth = activeCanvasIFrame?.getBoundingClientRect().width;
        const parentDiChildren = parentDi.children;

        if (parentDiChildren && parentDi?.item?.name === "Panel") {
          parentDiChildren.sort((a, b) => {
            if (a.fixedPositionInfo?.left && b.fixedPositionInfo?.left) {
              return a.fixedPositionInfo.left - b.fixedPositionInfo.left;
            }
            return 0;
          });

          parentDiChildren.forEach((child) => {
            if (
              child.fixedPositionInfo?.left &&
              fixedPositionInfo.left &&
              fixedPositionInfo.top &&
              absolutePositionInfo?.left &&
              absolutePositionInfo?.top
            ) {
              if (child.fixedPositionInfo.left === fixedPositionInfo.left) {
                const diWidth = di?.fixedPositionInfo?.width ?? 0;
                if (canvasWidth && fixedPositionInfo.left + diWidth + 50 < canvasWidth) {
                  fixedPositionInfo.left += 20;
                  absolutePositionInfo.left += 20;
                } else {
                  fixedPositionInfo.left = canvasWidth - (di.fixedPositionInfo?.width ?? 0) - 10;
                  absolutePositionInfo.left = canvasWidth - (di.fixedPositionInfo?.width ?? 0) - 10;
                }
                fixedPositionInfo.top += 20;
                absolutePositionInfo.top += 20;
              }
            }
          });
        }
      }

      const isHeightExpandableParent =
        parentDi?.item?.name === "Header" ||
        parentDi?.item?.name === "Footer" ||
        parentDi?.item?.name === "Page Header" ||
        parentDi?.item?.name === "Page Footer" ||
        parentDi?.item?.name === "Box" ||
        parentDi?.item?.name === "Panel";

      if (isHeightExpandableParent) {
        const isElementHeightHigherThanContainer = this.getElementHeightIsHigherThanContainer(
          fixedPositionInfo,
          parentDi,
          sd,
          absolutePositionInfo
        );
        if (isElementHeightHigherThanContainer && parentDi?.styleObj?.position?.height && fixedPositionInfo?.height) {
          parentDi.styleObj.position.height += fixedPositionInfo.height;
        }
      }

      const siblings: IDisplayItem[] = (parentDi ? parentDi.children : sd.displayItems) ?? [];
      const offset: IDuplicateOffset = resolveDuplicateOffset(
        {
          left: Number(fixedPositionInfo.left ?? 0),
          top: Number(fixedPositionInfo.top ?? 0),
          width: Number(fixedPositionInfo.width ?? 0),
          height: Number(fixedPositionInfo.height ?? 0)
        },
        siblings
          .filter((sibling: IDisplayItem): boolean => sibling.fixedPositionInfo !== undefined)
          .map((sibling: IDisplayItem): IOccupiedOrigin => ({
            left: Number(sibling.fixedPositionInfo?.left ?? 0),
            top: Number(sibling.fixedPositionInfo?.top ?? 0)
          })),
        pageWidth
      );
      fixedPositionInfo.left = Number(fixedPositionInfo.left ?? 0) + offset.dx;
      fixedPositionInfo.top = Number(fixedPositionInfo.top ?? 0) + offset.dy;
      if (absolutePositionInfo) {
        absolutePositionInfo.left = Number(absolutePositionInfo.left ?? 0) + offset.dx;
        absolutePositionInfo.top = Number(absolutePositionInfo.top ?? 0) + offset.dy;
      }

      newDisplayItem.fixedPositionInfo = fixedPositionInfo;
      newDisplayItem.absolutePositionInfo = absolutePositionInfo;
    }

    if (di.children?.length) {
      const queue: { oldDi: IDisplayItem; newDi: IDisplayItem }[] = [{ oldDi: di, newDi: newDisplayItem }];

      while (queue.length > 0) {
        const current = queue.shift();
        if (!current) continue;

        const { oldDi, newDi } = current;

        oldDi.children?.forEach((childDi) => {
          const newChildDi: IDisplayItem = {
            ...childDi,
            item: {
              ...childDi.item,
              events: [],
              properties: _.cloneDeep(childDi.item.properties)
            },
            children: [],
            parentId: newDi.id,
            id: ScreenDesignHelper.generateUniqueDisplayItemId(dict)
          };

          newChildDi.friendlyName = ScreenDesignHelper.generateUniqueFriendlyName(
            childDi.friendlyName || childDi.item.name || "Component",
            friendlyNames
          );

          ScreenDesignHelper.updateSymbolReferences(newChildDi, childDi.id, newChildDi.id);

          newDi.children.push(newChildDi);

          if (childDi.children?.length) {
            queue.push({ oldDi: childDi, newDi: newChildDi });
          }
        });
      }
    }

    ScreenDesignHelper.validateFriendlyNames(newDisplayItem);

    if (di.parentId) {
      const parent = dict[di.parentId];
      if (parent) {
        const curDiIndex = parent.children.indexOf(di);
        parent.children.splice(curDiIndex + 1, 0, newDisplayItem);
      }
    } else if (di && sd) {
      const curDiIndex = sd.displayItems.indexOf(di);
      sd.displayItems.splice(curDiIndex + 1, 0, newDisplayItem);
    }
  }

  static updateCloneChildDi(
    child: IDisplayItem,
    parentId: number,
    displayItems: IDisplayItem[],
    parentDI: IDisplayItem | undefined,
    dict: IScreenDesignDictionary
  ) {
    const friendlyNames = new Set<string>();
    Object.values(dict).forEach((di) => {
      ScreenDesignHelper.collectExistingFriendlyNames([di], friendlyNames);
    });

    const queue: { item: IDisplayItem; parentId: number; parentDI: IDisplayItem | undefined }[] = [
      { item: child, parentId, parentDI }
    ];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      const { item, parentId, parentDI } = current;

      item.parentId = parentId;

      const oldId = item.id;
      item.id = ScreenDesignHelper.generateUniqueDisplayItemId(dict);
      ScreenDesignHelper.updateSymbolReferences(item, oldId, item.id);

      item.friendlyName = ScreenDesignHelper.generateUniqueFriendlyName(
        item.friendlyName || item.item.name || "Component",
        friendlyNames
      );

      if (
        window.kuika?.isAbsoluteDesign === true &&
        parentDI &&
        item.fixedPositionInfo?.top &&
        item.absolutePositionInfo?.top
      ) {
        const calcTop = this.getCalculatedTopForDuplicateItem(parentDI, dict);
        if (calcTop) {
          item.absolutePositionInfo.top = calcTop + item.fixedPositionInfo.top;
        }
      }

      if (item.children?.length) {
        item.children.forEach((childItem) => {
          queue.push({
            item: childItem,
            parentId: item.id,
            parentDI: item
          });
        });
      }
    }

    return child;
  }

  static getCalculatedTopForDuplicateItem = (panelDI: IDisplayItem, dict: IScreenDesignDictionary) => {
    let calculatedTop = 0;
    // const rootDI = ScreenDesignHelper.findDisplayItemBykuikaid(SpecialDIIds.rootDI.toString(), sd?.displayItems);
    // const headerDI = ScreenDesignHelper.findDisplayItemBykuikaid(SpecialDIIds.headerDI.toString(), sd?.displayItems);
    const rootDI = dict[SpecialDIIds.rootDI];
    const headerDI = dict[SpecialDIIds.headerDI];
    if (!panelDI || !rootDI) return;
    if (!panelDI || !rootDI) return;
    for (let i = 0; i < rootDI.children.length; i++) {
      const child = rootDI.children[i];
      if (child && child.item.name === "Panel" && child.styleObj?.position?.height) {
        calculatedTop += child.styleObj?.position?.height;
      } else if (
        child.item.name === "Table" ||
        child.item.name === "ReportTable" ||
        child.item.name === "StaticTable"
      ) {
        const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
        const tableEl = activeCanvasIFrame?.contentWindow?.document.getElementById(
          `kuika_${child.id.toString()}`
        ) as HTMLElement;
        if (tableEl) {
          const tableHeight = tableEl.offsetHeight;
          calculatedTop += tableHeight;
        }
      }
    }
    if (headerDI && headerDI.styleObj?.position?.height) {
      calculatedTop += headerDI.styleObj?.position?.height;
    }
    const pageHeaderDI = dict[SpecialDIIds.pageHeaderDI];
    if (pageHeaderDI && pageHeaderDI.styleObj?.position?.height) {
      calculatedTop += pageHeaderDI.styleObj?.position?.height;
    }
    return calculatedTop;
  };

  static convertProjectDTOToInterface(dto: IProjectDto): IProject | undefined {
    const project = {} as IProject;
    if (!dto) {
      return undefined;
    }
    let json: IProjectMeta;
    try {
      json = JSON.parse(dto.json);
    } catch {
      return undefined;
    }
    if (!json) {
      return undefined;
    }
    const componentSetId = json.componentSetObj;
    const componentSet = {} as IComponentSet;
    componentSet.id = componentSetId;
    project.id = dto.id;
    project.name = dto.name;
    project.description = dto.description;
    project.domainId = dto.kDomain_Id;
    project.componentSetObj = componentSet;
    project.amIOwnerOfThisProject = dto.amIOwnerOfThisProject;
    project.haveIDeleteAccess = dto.haveIDeleteAccess;
    project.haveIEditAccess = dto.haveIEditAccess;
    project.themeName = json.themeName;
    project.configurations = json.configurations;
    project.scheduledJobs = json.scheduledJobs;
    project.apiManagements = json.apiManagements;
    project.selectedConfigurationId = json.selectedConfigurationId;
    project.lastRunConfigId = json.lastRunConfigId;
    project.startingScreenId = json.startingScreenId;
    project.signinScreenId = json.signinScreenId;
    project.resetPasswordScreenId = json.resetPasswordScreenId;
    project.leftMenuScreenId = json.leftMenuScreenId;
    project.splashScreenId = json.splashScreenId;
    project.newPasswordScreenId = json.newPasswordScreenId;
    project.verificationCodeScreenId = json.verificationCodeScreenId;
    project.offlineScreenId = json.offlineScreenId;
    project.documentUploadLinkScreenId = json.documentUploadLinkScreenId;
    project.processStepScreenIds = json.processStepScreenIds || [];
    project.stylingTypography = json.stylingTypography;
    project.stylingBorder = json.stylingBorder;
    project.stylingShadow = json.stylingShadow;
    project.stylingPosition = json.stylingPosition;
    project.stylingFill = json.stylingFill;
    project.stylingEffects = json.stylingEffects;
    project.roles = json.roles;
    project.languages = json.languages;
    project.screenFolders = json.screenFolders;
    project.roleFolders = json.roleFolders;
    project.actionFolders = json.actionFolders;
    project.projectType = json.projectType;
    project.passwordPolicies = json.passwordPolicies;
    project.smtpSettings = json.smtpSettings;
    project.awsSettings = json.awsSettings;
    project.paymentMethods = json.paymentMethods;
    project.permissionLabels = json.permissionLabels;
    project.googleMapsSettings = json.googleMapsSettings;
    project.appStoreConnectApiSettings = json.appStoreConnectApiSettings;
    project.googlePlayApiSettings = json.googlePlayApiSettings;
    project.inversCloudBoxxSettings = json.inversCloudBoxxSettings;
    project.commonMobileSettings = json.commonMobileSettings;
    project.formAuthenticationSettings = json.formAuthenticationSettings;
    project.loginOptions = json.loginOptions;
    project.firebaseSettings = json.firebaseSettings;
    project.pushNotificationSettings = json.pushNotificationSettings;
    project.reportCanvasSizes = json.reportCanvasSizes;
    project.lastVisitedScreenId = json.lastVisitedScreenId;
    project.screenCanvasSizes = json.screenCanvasSizes;
    project.savedColors = json.savedColors;
    project.workflowScreensSettings = json.workflowScreensSettings;
    project.projectAccess = dto.projectAccess;
    project.searchServiceSettings = json.searchServiceSettings;
    project.projectTranslations = json.projectTranslations;
    project.startProcessTriggers = json.startProcessTriggers ?? {};
    project.commonReservedKeys = dto.commonReservedKeys ?? [];
    return project;
  }

  static convertScreenListDTOToInfoInterface(screenDesignInfoDto: IScreenDesignInfoDto[]): IScreenDesignInfo[] {
    const arr = [] as IScreenDesignInfo[];

    screenDesignInfoDto.forEach((x) => {
      const obj = {} as IScreenDesignInfo;
      obj.id = x.id;
      obj.name = x.name;

      let parsedJson = {} as IScreenDesignPublicJSON;
      try {
        if (x.publicJson) {
          parsedJson = JSON.parse(x.publicJson);
        }
      } catch (e: any) {
        console.log("Couldn't parse public json.");
      }
      if (obj.publicMetaData === undefined) {
        obj.publicMetaData = {};
      }
      obj.publicMetaData.screenInputs = parsedJson && parsedJson.screenInputs ? parsedJson.screenInputs : [];
      obj.publicMetaData.screenOutputs = parsedJson && parsedJson.screenOutputs ? parsedJson.screenOutputs : [];
      obj.publicMetaData.screenType = parsedJson && parsedJson.screenType ? parsedJson.screenType : undefined;
      obj.publicMetaData.masterId = parsedJson && parsedJson.masterId ? parsedJson.masterId : undefined;
      obj.publicMetaData.supportOffline = parsedJson && parsedJson.supportOffline ? parsedJson.supportOffline : false;
      obj.publicMetaData.shouldRefresh = parsedJson?.shouldRefresh !== false;
      obj.publicMetaData.dataCacheEnabled = parsedJson?.dataCacheEnabled === true;
      obj.publicMetaData.reportParameters = parsedJson?.reportParameters ?? [];
      arr.push(obj);
    });

    return arr;
  }

  static convertProjectInterfaceToProjectMeta(project: IProject): IProjectMeta {
    const projectMeta = {} as IProjectMeta;

    if (!project) {
      throw new Error("Project is not defined.");
    }

    projectMeta.componentSetObj = project?.componentSetObj?.id;
    projectMeta.themeName = project.themeName;
    projectMeta.configurations = project.configurations;
    projectMeta.scheduledJobs = project.scheduledJobs;
    projectMeta.apiManagements = project.apiManagements;
    projectMeta.selectedConfigurationId = project.selectedConfigurationId;
    projectMeta.lastRunConfigId = project.lastRunConfigId;
    projectMeta.startingScreenId = project.startingScreenId;
    projectMeta.leftMenuScreenId = project.leftMenuScreenId;
    projectMeta.splashScreenId = project.splashScreenId;
    projectMeta.newPasswordScreenId = project.newPasswordScreenId;
    projectMeta.verificationCodeScreenId = project.verificationCodeScreenId;
    projectMeta.offlineScreenId = project.offlineScreenId;
    projectMeta.documentUploadLinkScreenId = project.documentUploadLinkScreenId;
    projectMeta.processStepScreenIds = project.processStepScreenIds || [];
    projectMeta.signinScreenId = project.signinScreenId;
    projectMeta.resetPasswordScreenId = project.resetPasswordScreenId;
    projectMeta.stylingTypography = project.stylingTypography;
    projectMeta.stylingBorder = project.stylingBorder;
    projectMeta.stylingShadow = project.stylingShadow;
    projectMeta.stylingPosition = project.stylingPosition;
    projectMeta.stylingFill = project.stylingFill;
    projectMeta.stylingEffects = project.stylingEffects;
    projectMeta.roles = project.roles;
    projectMeta.languages = project.languages;
    projectMeta.screenFolders = project.screenFolders;
    projectMeta.roleFolders = project.roleFolders;
    projectMeta.actionFolders = project.actionFolders;
    projectMeta.projectType = project.projectType;
    projectMeta.passwordPolicies = project.passwordPolicies;
    projectMeta.smtpSettings = project.smtpSettings;
    projectMeta.awsSettings = project.awsSettings;
    projectMeta.paymentMethods = project.paymentMethods;
    projectMeta.permissionLabels = project.permissionLabels;
    projectMeta.googleMapsSettings = project.googleMapsSettings;
    projectMeta.appStoreConnectApiSettings = project.appStoreConnectApiSettings;
    projectMeta.googlePlayApiSettings = project.googlePlayApiSettings;
    projectMeta.inversCloudBoxxSettings = project.inversCloudBoxxSettings;
    projectMeta.commonMobileSettings = project.commonMobileSettings;
    projectMeta.formAuthenticationSettings = project.formAuthenticationSettings;
    projectMeta.loginOptions = project.loginOptions;
    projectMeta.firebaseSettings = project.firebaseSettings;
    projectMeta.pushNotificationSettings = project.pushNotificationSettings;
    projectMeta.reportCanvasSizes = project.reportCanvasSizes;
    projectMeta.lastVisitedScreenId = project.lastVisitedScreenId;
    projectMeta.screenCanvasSizes = project.screenCanvasSizes;
    projectMeta.savedColors = project.savedColors;
    projectMeta.projectAccess = project.projectAccess;
    projectMeta.workflowScreensSettings = project.workflowScreensSettings;
    projectMeta.searchServiceSettings = project.searchServiceSettings as ISearchServiceSettings[];
    projectMeta.projectTranslations = project.projectTranslations;
    projectMeta.startProcessTriggers = project.startProcessTriggers ?? {};

    return projectMeta;
  }

  static convertSDDTOToIScreenDesignInterface(dto: IScreenDesignDto): IScreenDesign {
    let iScreen = {} as IScreenDesign;
    if (dto.json) {
      let json: IScreenDesignMeta;
      try {
        json = JSON.parse(dto.json);
      } catch {
        return iScreen;
      }
      iScreen.displayItems = json.displayItems as IDisplayItem[];
      iScreen.onClickActionComponentId = json?.onClickActionComponentId;
      iScreen.shortcuts = json?.shortcuts ?? {};

      if (!json.devices) {
        json.devices = { phone: true, tablet: true, desktop: true };
      } else {
        if ((json.devices as any).Phone && !json.devices.phone) json.devices.phone = (json.devices as any).Phone;
        if ((json.devices as any).Tablet && !json.devices.tablet) json.devices.tablet = (json.devices as any).Tablet;
        if ((json.devices as any).Desktop && !json.devices.desktop)
          json.devices.desktop = (json.devices as any).Desktop;
      }

      iScreen.devices = json.devices;
      iScreen.isAbsoluteDesign = json.isAbsoluteDesign;
      iScreen.languages = json.languages;
      iScreen.authorization = json.authorization;
      if ((dto as any).publicJSON) {
        try {
          const publicJson: IScreenDesignPublicJSON = JSON.parse((dto as any).publicJSON);
          (iScreen as any).isWebScreen = publicJson?.screenType === 4;
          // Keep the whole blob, not just the one flag: this response is the screen's OWN
          // row (entity-materialized → branch-overlay correct) and it arrives together with
          // the tree, so it is the only metadata source that cannot disagree with sdId. The
          // save projects publicJson from here instead of from the project screen list,
          // whose entry is missing during the load window and stale on a non-master branch.
          iScreen.publicMetaData = publicJson;
        } catch (e) {
          console.error("Failed to parse screen publicJson:", e);
        }
      }
    } else {
      let defaultDi: any = [
        {
          id: -1,
          itemType: 1,
          styleObj: {
            fill: {
              fillIds: ["b469ca57-a59d-48ae-847f-592fc99a7b99"],
              fillId: null,
              repeatSwitch: false,
              bgImageSize: false,
              stretch: false,
              imageFit: undefined,
              bgImagePosition: "left"
            }
          },
          item: {
            id: "HTML_DIV",
            properties: [
              {
                name: "style",
                selectedType: 2,
                value: ""
              }
            ]
          },
          children: [],
          screenDesign: "[Circular ~]"
        }
      ];
      iScreen.displayItems = [];
      iScreen.displayItems.push(...defaultDi);
      iScreen.devices = { phone: true, tablet: true, desktop: true };

      const authorization: IAuthorizationDto = {
        permittedRoles: [],
        allowAnonymousAccess: true,
        allowAllRoles: true,
        hide: false,
        disable: false
      };
      iScreen.authorization = authorization;
    }

    iScreen.id = dto.id;
    iScreen.name = dto.name;
    iScreen.projectId = dto.kProject_Id;

    return iScreen;
  }

  static isItSampleExcelRow(arr: string[]): boolean {
    let found = false;
    KMainFunctions.excelTitles.forEach((eT) => {
      if (arr.indexOf(eT) > -1) {
        found = true;
      } else {
        found = false;
      }
    });
    return found;
  }

  static convertExcelRowToIComponent(arr: any[]): IExcelRowToComponentResult {
    const result: IExcelRowToComponentResult = {
      component: {
        id: KMainFunctions.generateGuid().toString(),
        name: "",
        componentSetId: "",
        isItChildItem: false,
        hasValidate: false,
        isItNavigationComponent: false,
        isItHiddenFromComponentList: false,
        appSupportType: EAppSupportType.All,
        isDeprecated: false,
        isRenamingDisabled: false,
        wrapWithFormItem: false,
        blockStyle: BlockStyle.block,
        tagName: "",
        componentType: ComponentType.simple,
        properties: [],
        data: "",
        methods: "",
        children: [],
        addChildText: "",
        removeChildText: "",
        dropAreaText: "",
        childrenInfo: [],
        icon: "",
        titleAttributeName: "",
        inVisibleStyles: [],
        isReportDesignComponent: false,
        dragDropAreaText: "",
        isDataRepeater: false
      },
      errorFound: false,
      errorString: [""]
    };

    arr.forEach((colItem, idx) => {
      let value = colItem;
      if (KMainFunctions.excelTitles[idx] === "blockStyle") {
        value = BlockStyle[colItem];
      } else if (KMainFunctions.excelTitles[idx] === "componentType") {
        value = ComponentType[colItem];
      } else if (KMainFunctions.excelTitles[idx] === "properties") {
        value = JSON.parse(value) as IProperty[];
        if (value && value.length && value.length > 0) {
          value.forEach((p) => {
            if (!p.name) {
              result.errorFound = true;
              result.errorString.push("Property name not found!");
            }
            if (!p.type) {
              result.errorFound = true;
              result.errorString.push("Property type not found!");
            } else if (!p.type.length) {
              result.errorFound = true;
              result.errorString.push("Property type not found!");
            }
            if (p.isRequired === true && p.value === "") {
              result.errorFound = true;
              result.errorString.push("Required property value could not found!");
            }
            p.id = KMainFunctions.generateDisplayItemId();
            if (p.type && p.type.length === 1) {
              p.selectedType = p.type[0];
              if (
                p &&
                p.typeOptions &&
                KMainFunctions.isObjectEmpty(p.typeOptions) === false &&
                p.typeOptions.length === 1
              ) {
                p.typeOptions = p.typeOptions as IPropertyTypeOptions[];
                p.hasOptions = p.typeOptions[0].hasOptions;
                p.options = p.typeOptions[0].options;
              }
            } else if (p.type && p.typeOptions) {
              p.typeOptions = p.typeOptions as IPropertyTypeOptions[];
              if (!p.selectedType) {
                p.selectedType = -1;
              }
            }
          });
        }
      } else if (KMainFunctions.excelTitles[idx] === "childrenInfo") {
        try {
          value = JSON.parse(value);
        } catch (e: any) {
          value = value.split(",");
          const childArr: IComponentChild[] = [];
          value.forEach((v) => {
            if (v?.length > 0) {
              const childInfo = {} as IComponentChild;
              childInfo.name = v;
              childInfo.count = 1;
              childArr.push(childInfo);
            }
          });
          value = childArr;
        }
      } else if (
        (KMainFunctions.excelTitles[idx] === "isItChildItem" ||
          KMainFunctions.excelTitles[idx] === "hasValidate" ||
          KMainFunctions.excelTitles[idx] === "isItNavigationComponent" ||
          KMainFunctions.excelTitles[idx] === "isItHiddenFromComponentList" ||
          KMainFunctions.excelTitles[idx] === "appSupportType" ||
          KMainFunctions.excelTitles[idx] === "isDeprecated" ||
          KMainFunctions.excelTitles[idx] === "isRenamingDisabled" ||
          KMainFunctions.excelTitles[idx] === "wrapWithFormItem") &&
        typeof value === "string"
      ) {
        if (value.toLowerCase() === "true") {
          value = true;
        } else {
          value = false;
        }
      }
      result.component[KMainFunctions.excelTitles[idx]] = value;
      if (KMainFunctions.excelTitles[idx] === "childrenInfo") {
        result.component.childrenNames = [];
        if (value?.length > 0) {
          result.component.childrenNames = value.map((x) => x.name);
        }
      }
    });
    return result;
  }

  static generateGuid = () => {
    return Guid.create().toString();
  };

  static removeNeededFieldsForTreeDI = (di) => {
    delete di.title;
    delete di.expanded;
    delete di.parentId;
    if (di.children) {
      di.children.forEach((child) => {
        KMainFunctions.removeNeededFieldsForTreeDI(child);
      });
    }
  };

  static getNewAddedNodesInTree = (di, nodes) => {
    if (di.id === -1 && di.parentId) {
      nodes.push(di);
    }
    if (di.children) {
      di.children.forEach((child) => {
        KMainFunctions.getNewAddedNodesInTree(child, nodes);
      });
    }
    return nodes;
  };

  static convertCamelCaseToFriendlyName = (value: string) => {
    const len = value.length;
    let res = "";
    for (let i = 0; i < len; i++) {
      let char = value[i];
      if (i === 0) {
        char = char.toUpperCase();
      }
      if (char === char.toUpperCase() && i !== 0) {
        res = `${res} ${char}`;
      } else {
        res += char;
      }
    }
    return res;
  };

  static capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  static formatRelativeTime = (date: string | Date): string => {
    const now = dayjs();
    const then = dayjs(date);
    const diffSeconds = now.diff(then, "second");
    const diffMinutes = now.diff(then, "minute");
    const diffHours = now.diff(then, "hour");
    const diffDays = now.diff(then, "day");
    const diffWeeks = now.diff(then, "week");
    const diffMonths = now.diff(then, "month");
    const diffYears = now.diff(then, "year");

    if (diffSeconds < 60) return "just now";
    if (diffMinutes < 60) return diffMinutes === 1 ? "a minute ago" : `${diffMinutes} minutes ago`;
    if (diffHours < 24) return diffHours === 1 ? "an hour ago" : `${diffHours} hours ago`;
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks < 5) return diffWeeks === 1 ? "a week ago" : `${diffWeeks} weeks ago`;
    if (diffMonths < 12) return diffMonths === 1 ? "a month ago" : `${diffMonths} months ago`;
    return diffYears === 1 ? "a year ago" : `${diffYears} years ago`;
  };

  /**
   * `formatRelativeTime`'s mirror for a FUTURE instant ("in 4 min", "in 3 h") — a countdown
   * ladder, not a calendar. Deliberately timezone-free: it only ever diffs two instants, so it
   * stays deterministic given `now` without depending on the `dayjs` `utc`/`timezone` plugins
   * (extended once, in `index.tsx`, which a unit test never imports). A caller that needs a
   * calendar-aware refinement ("Tomorrow 09:00" in a specific zone) computes that itself and
   * falls back to this for everything else — see `describeScheduledJobNextRun`.
   */
  static formatRelativeFutureTime = (date: string | Date, now: number = Date.now()): string => {
    const from: dayjs.Dayjs = dayjs(now);
    const to: dayjs.Dayjs = dayjs(date);
    const diffSeconds: number = to.diff(from, "second");
    const diffMinutes: number = to.diff(from, "minute");
    const diffHours: number = to.diff(from, "hour");
    const diffDays: number = to.diff(from, "day");
    const diffWeeks: number = to.diff(from, "week");
    const diffMonths: number = to.diff(from, "month");
    const diffYears: number = to.diff(from, "year");

    if (diffSeconds <= 0) return "now";
    if (diffSeconds < 60) return "in a moment";
    if (diffMinutes < 60) return diffMinutes === 1 ? "in a minute" : `in ${diffMinutes} min`;
    if (diffHours < 24) return diffHours === 1 ? "in an hour" : `in ${diffHours} h`;
    // Never "tomorrow": this ladder only diffs two instants and has no calendar, so 30 hours after
    // 02:00 would be called tomorrow while it is the day after. A caller with a zone says it.
    if (diffDays < 7) return diffDays === 1 ? "in a day" : `in ${diffDays} days`;
    if (diffWeeks < 5) return diffWeeks === 1 ? "in a week" : `in ${diffWeeks} weeks`;
    if (diffMonths < 12) return diffMonths === 1 ? "in a month" : `in ${diffMonths} months`;
    return diffYears === 1 ? "in a year" : `in ${diffYears} years`;
  };

  static formatCamelCase = (str: string): string => {
    let result = str
      .replace(/([A-Z])([A-Z])([a-z])/g, "$1 $2$3")
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

    const lowercaseWords = ["to", "a", "an", "of", "in", "on", "at", "by", "or", "and"];
    const words = result.split(" ");

    return words
      .flatMap((word, index) => {
        if (index === 0) {
          return [word];
        }

        for (const commonWord of lowercaseWords) {
          if (word.toLowerCase().startsWith(commonWord)) {
            const nextChar = word[commonWord.length];
            if (nextChar && /[A-Z]/.test(nextChar)) {
              return [commonWord.toLowerCase(), word.substring(commonWord.length)];
            }
          }
        }

        if (lowercaseWords.includes(word.toLowerCase())) {
          return [word.toLowerCase()];
        }

        return [word];
      })
      .join(" ");
  };

  private static readonly COLOR_PICKER_CLASS = "kuika_colorPicker";

  private static readonly MAX_PICKER_SEARCH_DEPTH = 5;

  private static readonly COLOR_PICKER_HEIGHT = 330;

  private static readonly COLOR_PICKER_GRADIENT_HEIGHT = 170;

  static setColorTriggeredPickerPosition(event: MouseEvent, type?: EColorPickerTab): void {
    const parentElement = (event.currentTarget as HTMLElement)?.parentElement;
    if (!parentElement) return;

    const colorPicker = this.findColorPicker(parentElement);
    if (!colorPicker) return;

    const screenHeight = window.innerHeight;
    const cursorY = event.clientY;
    const pickerHeight =
      type === EColorPickerTab.Gradient ? this.COLOR_PICKER_GRADIENT_HEIGHT : this.COLOR_PICKER_HEIGHT;
    const pickerTop = cursorY + pickerHeight > screenHeight ? cursorY - pickerHeight : cursorY;

    colorPicker.style.top = `${pickerTop}px`;
  }

  private static findColorPicker(element: HTMLElement | null, depth = 0): HTMLElement | null {
    if (!element || depth > this.MAX_PICKER_SEARCH_DEPTH) return null;

    const picker = element.getElementsByClassName(this.COLOR_PICKER_CLASS)[0] as HTMLElement;
    return picker || this.findColorPicker(element.parentElement, depth + 1);
  }

  static displaySuccessNotification(message, params?) {
    toastService.success(message, params?.title);
  }

  static displayInfoNotification(message, params?) {
    toastService.info(message, params?.title);
  }

  static displayWarningNotification(message, params?) {
    toastService.warning(message, params?.title);
  }

  static displayCompilerErrors(errors: string[]): void {
    if (errors.length === 0) return;
    const message: string =
      errors.length === 1
        ? errors[0]
        : `${errors.length} compilation errors:\n${errors.map((error: string) => `• ${error}`).join("\n")}`;
    this.displayWarningNotification(message);
  }

  static displayErrorNotification(message, params?) {
    if (message) {
      toastService.error(message, params?.title);
    }
  }

  static isThumbnailGenerationRequired(id: string) {
    if (domainIds.indexOf(id) > -1) {
      return false;
    }
    return true;
  }

  static async clearIndexedDbAutosaveData() {
    try {
      const { openDB } = await import("idb");
      const db = await openDB<IAutosaveDbDto>("autosave-db", 1, {
        upgrade(db) {
          db.createObjectStore("autosave-data");
        }
      });

      try {
        await db.clear("autosave-data");
      } finally {
        db.close();
      }
    } catch (e) {
      console.error("Failed to clear IndexedDB autosave data:", e);
    }
  }

  static async clearIndexedDbAutosaveDataExceptKey(keyToInclude: string) {
    try {
      const { openDB } = await import("idb");
      const db = await openDB<IAutosaveDbDto>("autosave-db", 1, {
        upgrade(db) {
          db.createObjectStore("autosave-data");
        }
      });

      try {
        const store = db.transaction("autosave-data").objectStore("autosave-data");
        const keys = await store.getAllKeys();

        const keysToDelete = keys.filter((x) => x.indexOf(keyToInclude) === -1);

        await Promise.all(keysToDelete.map((x) => db.delete("autosave-data", x)));
      } finally {
        db.close();
      }
    } catch (e) {
      console.error("Failed to clear IndexedDB autosave data:", e);
    }
  }

  static getMultilanguageValue(key: string, language: string): string {
    try {
      const phrase = multiLanguage[key];
      if (phrase) {
        return phrase[language] ? phrase[language] : phrase.en;
      }
      return "";
    } catch {
      return "";
    }
  }

  static getBlockStyleName(blockStyle: BlockStyle): string {
    if (blockStyle === BlockStyle.block) {
      return "block";
    }
    if (blockStyle === BlockStyle.inlineBlock) {
      return "inlineBlock";
    }
    return "";
  }

  static getComponentType(componentType: ComponentType): string {
    if (componentType === ComponentType.simple) {
      return "simple";
    }
    if (componentType === ComponentType.responsiveRow) {
      return "responsiveRow";
    }
    if (componentType === ComponentType.responsiveColumn) {
      return "responsiveColumn";
    }
    if (componentType === ComponentType.itemContainer) {
      return "itemContainer";
    }
    if (componentType === ComponentType.container) {
      return "container";
    }
    if (componentType === ComponentType.abstractContainer) {
      return "abstractContainer";
    }
    if (componentType === ComponentType.none) {
      return "none";
    }
    if (componentType === ComponentType.abstractItemContainer) {
      return "abstractItemContainer";
    }
    if (componentType === ComponentType.noneEditable) {
      return "noneEditable";
    }

    return "";
  }

  static exceptionHandler(e) {
    const msg = e?.response?.data?.message;
    if (msg) {
      this.displayErrorNotification(msg);
    } else if (e?.message) {
      console.error(e.message);
      this.displayErrorNotification(e.message);
    }
    if (e?.stack) {
      console.error(e.stack);
    }
  }

  /**
   * Extracts the human-readable message the backend put in an error response body
   * (`{ message: "..." }`), so callers can surface the real cause instead of a
   * generic fallback. Returns undefined for network/transport errors that carry no
   * response body.
   */
  static getApiErrorMessage(error: unknown): string | undefined {
    const apiError: IApiErrorLike | undefined = error as IApiErrorLike | undefined;
    return apiError?.response?.data?.message;
  }

  static warningHandler(e) {
    const msg = e?.response?.data?.message;
    if (msg) {
      this.displayWarningNotification(msg);
    } else if (e?.message) {
      console.warn(e.message);
      this.displayWarningNotification(e.message);
    }
    if (e?.stack) {
      console.warn(e.stack);
    }
  }

  static getUserNameToShow = (user: IUserInfoDto | IUser, full: boolean): string => {
    let str = "";

    if (user.firstName?.length > 0) {
      if (full === true) {
        str += user.firstName;
      } else {
        str += `${user.firstName.substring(0, 1)}.`;
      }
      if (user.lastName?.length > 0) {
        if (full === true) {
          str += " ";
        }
        str += user.lastName;
      }
    } else {
      str = user.username;
    }

    return str;
  };

  static getUserLettersToShow = (user: IUserInfoDto | IUser | IProjectUserRole): string => {
    if (KMainFunctions.isObjectEmpty(user)) return "";
    let str = "";
    // Accepts both the IUserInfoDto/IUser shape (firstName/lastName/username) and the enriched
    // IProjectUserRole row (userFirstName/userLastName/userName); narrow by which fields are present.
    if ("firstName" in user && user.firstName?.length > 0) str += user.firstName.substring(0, 1);
    if ("userFirstName" in user && user.userFirstName?.length > 0) str += user.userFirstName.substring(0, 1);
    if ("lastName" in user && user.lastName?.length > 0) str += user.lastName.substring(0, 1);
    if ("userLastName" in user && user.userLastName?.length > 0) str += user.userLastName.substring(0, 1);
    if (str.length === 0) {
      if ("username" in user && user.username?.length > 0) str += user.username.substring(0, 1);
      else if ("userName" in user && user.userName?.length > 0) str += user.userName.substring(0, 1);
    }

    return str.toUpperCase();
  };

  static showResizeWrapper = () => {
    const resizeWrapper: HTMLElement = document.getElementsByClassName("kuika_resizeWrapper")[0] as HTMLElement;

    if (resizeWrapper) {
      resizeWrapper.style.display = "block";
    }
  };

  static hideResizeWrapper = () => {
    const resizeWrapper: HTMLElement = document.getElementsByClassName("kuika_resizeWrapper")[0] as HTMLElement;

    if (resizeWrapper) {
      resizeWrapper.style.display = "none";
    }
  };

  static isArrHasValue = (arr: string[] | undefined): boolean => {
    if (arr === undefined) return false;
    if (arr?.length > 0 && arr.filter((x) => x !== "").length === arr.length) return true;
    return false;
  };

  static getSubscriptionIdFromUrl = (): string => {
    let { pathname } = window.location;
    pathname = KMainFunctions.removeLastCharIfEndsWith(pathname, "/");
    const splittedPathname = pathname.split("/");
    return splittedPathname[splittedPathname.length - 1];
  };

  static getPlanIdFromUrl = (): string => {
    let { pathname } = window.location;
    pathname = KMainFunctions.removeLastCharIfEndsWith(pathname, "/");
    const splittedPathname = pathname.split("/");
    return splittedPathname[splittedPathname.length - 1];
  };

  static removeLastCharIfEndsWith = (str: string, char: string): string => {
    if (str?.endsWith(char)) {
      str = str.slice(0, -1);
    }
    return str;
  };

  static getSdIdFromUrl = (): string => {
    const pathname = KMainFunctions.removeLastCharIfEndsWith(window.location.pathname, "/");
    const splittedPathname = pathname.split("/");
    if (splittedPathname.length > 2 && splittedPathname[splittedPathname.length - 2] === "sd") {
      return splittedPathname[splittedPathname.length - 1];
    }
    return "";
  };

  static selectedDevice;

  static dashboardMode;

  static selectedDeviceType = (val, dashboardState) => {
    KMainFunctions.selectedDevice = val;
    KMainFunctions.dashboardMode = dashboardState;
  };

  static focusToFrame = (): void => {
    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (activeCanvasIFrame) {
      activeCanvasIFrame.focus();
    }
  };

  static waitAndResize = _.debounce(
    (display: boolean, toolbarIsHidden: boolean, sd: IScreenDesignContextState, zoomScale?: number) =>
      KMainFunctions.onResize(display, toolbarIsHidden, sd, zoomScale),
    4
  );

  /** The report canvas's current zoom, for the resize passes that arrive without it. */
  static reportZoomScale: number = 1;

  /**
   * How much wider than the page the report frame has to be so the page is not squeezed.
   *
   * The page scrolls vertically inside `.kuika_rootDIWrapper`, and a classic (space-taking)
   * scrollbar eats into the frame's content width — a frame exactly one page wide then shows a
   * horizontal scrollbar over a page that fits. Measured rather than assumed because overlay
   * scrollbars (macOS default) take no space at all; a fixed allowance would leave a permanent
   * gap there. Returns 0 until the agent document exists.
   */
  static reportFrameScrollbarAllowance = (canvasIFrame: HTMLIFrameElement): number => {
    const scroller: HTMLElement | null | undefined =
      canvasIFrame.contentDocument?.querySelector(".kuika_rootDIWrapper");
    if (!scroller) {
      return 0;
    }
    return Math.max(0, scroller.offsetWidth - scroller.clientWidth);
  };

  static onResize = (
    display: boolean,
    toolbarIsHidden: boolean,
    sd: IScreenDesignContextState,
    zoomScale?: number
  ): void => {
    const kuika_iframeWrapper: HTMLIFrameElement = document.getElementsByClassName(
      "kuika_iframeWrapper"
    )[0] as HTMLIFrameElement;
    const activeCanvasIFrame: HTMLIFrameElement = document.getElementById(
      "kuika_iframeAgent_canvas"
    ) as HTMLIFrameElement;

    if (!activeCanvasIFrame || !kuika_iframeWrapper) {
      return;
    }

    activeCanvasIFrame.dataset.visible = String(display);

    // When not displaying, skip repositioning — getBoundingClientRect returns {0,0}
    // on display:none elements and would misplace the iframe over the toolbar.
    if (!display) {
      return;
    }

    const x: number | undefined = kuika_iframeWrapper?.getBoundingClientRect().x;
    const y: number | undefined = kuika_iframeWrapper?.getBoundingClientRect().y;
    const width: number | undefined = kuika_iframeWrapper?.getBoundingClientRect().width;
    const height: number | string | undefined = kuika_iframeWrapper?.getBoundingClientRect().height;

    activeCanvasIFrame.style.borderRadius = "0px";

    if (x !== undefined) {
      activeCanvasIFrame.style.left = `${x.toString()}px`;
    }
    if (y !== undefined) {
      activeCanvasIFrame.style.top = `${y.toString()}px`;
    }

    const isReportCanvas: boolean = sd.dashboardState === 17;

    // Only the canvas knows the zoom, and not every caller comes from there — a panel drag, for
    // one, calls in without it. Remembering the last one keeps those passes from re-sizing the
    // frame as if zoom were 1 and wiping its transform. Forgotten again the moment another
    // surface takes the canvas, so the next report cannot inherit the previous one's zoom.
    if (!isReportCanvas) {
      KMainFunctions.reportZoomScale = 1;
    } else if (zoomScale !== undefined) {
      KMainFunctions.reportZoomScale = zoomScale;
    }
    const effectiveZoomScale: number = isReportCanvas ? (zoomScale ?? KMainFunctions.reportZoomScale) : 1;

    if (isReportCanvas && width !== undefined) {
      // The wrapper is already exactly as wide as the page allows — the design area sizes it
      // from the paper and clamps it to the centre column — so the frame simply follows it,
      // plus whatever the page's scrollbar takes. Written unconditionally (and never onto the
      // wrapper) so no earlier pass can leave a stale pin behind: the frame used to be pinned
      // for pages under 320px and only unpinned when a LATER measurement came back under 320,
      // which for anything wider never happened, so it stayed narrow and left-aligned with the
      // page scrolling inside it.
      const frameWidth: number =
        (width + KMainFunctions.reportFrameScrollbarAllowance(activeCanvasIFrame)) / effectiveZoomScale;
      activeCanvasIFrame.style.width = `${frameWidth}px`;
      activeCanvasIFrame.style.minWidth = `${frameWidth}px`;
      activeCanvasIFrame.style.maxWidth = `${frameWidth}px`;
      kuika_iframeWrapper.style.width = "";
      kuika_iframeWrapper.style.minWidth = "";
      kuika_iframeWrapper.style.maxWidth = "";
    } else if (width !== undefined) {
      activeCanvasIFrame.style.width = `${width.toString()}px`;
      // Always cleared, because the report branch above pins them on every pass: a UI screen
      // opened after a report would otherwise inherit that report's page width as a floor and
      // a ceiling.
      activeCanvasIFrame.style.maxWidth = "";
      activeCanvasIFrame.style.minWidth = "";
      if (width < 320) {
        kuika_iframeWrapper.style.width = "";
        kuika_iframeWrapper.style.maxWidth = "";
        kuika_iframeWrapper.style.minWidth = "";
      }
    }
    if (isReportCanvas && sd.project?.screenCanvasSizes) {
      const screen: IScreenCanvasSize | undefined = sd.project.screenCanvasSizes.screens?.find(
        (entry: IScreenCanvasSize): boolean => entry.id === sd.sdId
      );
      const pageHeight: number = getReportCanvasSize(screen).height;
      if (pageHeight < 570) {
        activeCanvasIFrame.style.height = `${pageHeight / effectiveZoomScale}px`;
        kuika_iframeWrapper.style.height = `${pageHeight}px`;
      } else {
        activeCanvasIFrame.style.height = `calc((100vh - 136px) / ${effectiveZoomScale})`;
        kuika_iframeWrapper.style.height = `calc(100vh - 136px)`;
      }
    } else if (height !== undefined) {
      if (typeof height === "string") {
        activeCanvasIFrame.style.height = height;
      } else {
        activeCanvasIFrame.style.height = `${height.toString()}px`;
      }

      if (kuika_iframeWrapper) {
        kuika_iframeWrapper.style.height = "";
        kuika_iframeWrapper.style.minWidth = "";
        kuika_iframeWrapper.style.maxWidth = "";
        kuika_iframeWrapper.style.width = "";
      }
    }

    if (isReportCanvas) {
      activeCanvasIFrame.style.transform = `scale(${effectiveZoomScale})`;
      activeCanvasIFrame.style.transformOrigin = "top left";
    } else if (activeCanvasIFrame.style.transform) {
      activeCanvasIFrame.style.transform = "";
      activeCanvasIFrame.style.transformOrigin = "";
    }
  };

  static isUserKuikaDeveloper = (username: string): boolean => {
    if (username && username.includes("@kuika.com")) return true;
    return false;
  };

  /**
   * A Col's width as persisted in `symbol.fixedValue`. The designer stores it as a string ("6"), but the
   * AI / MCP property tools can persist the same width as a JSON number (6); an absent width reads as "".
   * Reading it as a string unconditionally threw on every Row/Col selection and white-screened the
   * designer (KOA-18777).
   */
  private static toResponsiveColumnText = (value: unknown): string => {
    if (value === undefined || value === null) return "";
    return String(value);
  };

  static getResponsiveColumnStringValue = (str: unknown, newVal: number): string => {
    const re = new RegExp("([a-z|A-Z-]*)([0-9]+)([a-z|A-Z-]*)");
    const current: string = KMainFunctions.toResponsiveColumnText(str);
    if (!re.test(current)) return newVal.toString();
    return current.replace(re, `$1${newVal.toString()}$3`);
  };

  static getResponsiveColumnNumberValue = (str: unknown): string => {
    const re = new RegExp("([a-z|A-Z-]*)([0-9]+)([a-z|A-Z-]*)");
    return KMainFunctions.toResponsiveColumnText(str).replace(re, "$2");
  };

  static getCorrectJSONFromString = (jsonString: string) => {
    let result: any = {};
    let correctJson = jsonString.replace(/\s(?=([^"]*"[^"]*")*[^"]*$)/g, "");
    correctJson = correctJson.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');
    correctJson = correctJson.replace(/'/g, '"');
    if (correctJson !== "") {
      result = JSON.parse(correctJson);
    }
    return result;
  };

  static turkishCharsToEnglish = (str) => {
    return str
      ?.replace("Ğ", "G")
      ?.replace("Ü", "U")
      ?.replace("Ş", "S")
      ?.replace("İ", "I")
      ?.replace("Ö", "O")
      ?.replace("Ç", "C")
      ?.replace("ğ", "g")
      ?.replace("ü", "u")
      ?.replace("ş", "s")
      ?.replace("ı", "i")
      ?.replace("ö", "o")
      ?.replace("ç", "c");
  };

  static allowOnlyNumbers = (e: any, allowDecimals?: boolean) => {
    if (e.keyCode === 69 || e.key === "+" || e.key === "-" || e.key === "," || e.code === "Space") {
      return false;
    }

    if (allowDecimals && e.key === ".") {
      return true;
    }

    if (!allowDecimals && e.key === ".") {
      return false;
    }

    return true;
  };

  static isDomainHasAccess = (assetName: string, parameters?: any): boolean => {
    if (window?.location?.hostname !== "platform.kuika.com" && window?.location?.hostname !== "preprod.kuika.com") {
      return true;
    }
    if (this.isUserKuikaDeveloper(window?.userInfo?.username)) return true;
    const assets = window?.userInfo?.domainAccesses?.assets;
    if (assets && assets.length > 0) {
      const relatedAsset = assets?.find((x) => x.assetName == assetName);
      if (
        assetName === "Bluetooth" ||
        assetName === "AI Actions" ||
        assetName === "AI Agents" ||
        assetName === "Vision Action" ||
        assetName === "MQTT" ||
        assetName === "Google Blockly" ||
        assetName === "OData" ||
        assetName === "Swagger API" ||
        assetName === "REST API" ||
        assetName === "SAP"
      ) {
        return true;
      }
      if (relatedAsset?.hasAccess === true) {
        return true;
      }
      if (relatedAsset?.assetName === "Screens" || relatedAsset?.assetName === "Screen Per Project") {
        const data = relatedAsset?.projectScreenAccesses?.find((x) => x.projectId === window?.kuika?.activeProjectId);
        if (data?.hasScreenAddAccess === true) return true;
      }
    } else {
      // Free kullanıcı ise
      if (assetName === "Screens") {
        if (parameters && parameters >= 50) return false;
        return true;
      }
      if (assetName === "Actions") {
        if (parameters && parameters.length >= 100) return false;
        return true;
      }
      if (assetName === "Seat") {
        if (parameters === true) {
          return false;
        }
        return true;
      }
      if (
        assetName === "IOS" ||
        assetName === "Code Export" ||
        assetName === "Backend API" ||
        assetName === "Swagger API" ||
        assetName === "REST API" ||
        assetName === "OData" ||
        assetName === "Ticket Support" ||
        assetName === "Screen Templates Package" ||
        assetName === "Collaboration Tools" ||
        assetName === "Custom SSO" ||
        assetName === "OnPrem Deployment" ||
        assetName === "C# Functions" ||
        assetName === "Account Manager" ||
        assetName === "Workflow" ||
        assetName === "Process Automation" ||
        assetName === "Bluetooth" ||
        assetName === "AI Actions" ||
        assetName === "AI Agents" ||
        assetName === "Vision Action" ||
        assetName === "SAP"
      ) {
        return false;
      }
      if (assetName === "Android" || assetName === "Project" || assetName === "MQTT") {
        return true;
      }
    }
    return false;
  };

  static getElementHeightIsHigherThanContainer = (
    fixedPositionInfo: IFixedPosition | undefined,
    targetDI: IDisplayItem | undefined,
    sd: IScreenDesign,
    absolutePositionInfo?: IAbsolutePosition | undefined,
    isCreate?: boolean
  ) => {
    const isTargetDIHeader = CommonHelperFunctions.isTargetDIHeaderByPosition(sd, absolutePositionInfo?.top);
    if (isTargetDIHeader === false && targetDI?.id === SpecialDIIds.headerDI && !isCreate) {
      return;
    }
    if (
      fixedPositionInfo?.top &&
      fixedPositionInfo?.height &&
      targetDI &&
      targetDI.styleObj?.position?.height &&
      sd.isAbsoluteDesign === true
    ) {
      const diHeightDiffWithContainer =
        targetDI.styleObj?.position?.height - (fixedPositionInfo?.height + fixedPositionInfo?.top);
      if (diHeightDiffWithContainer < 0) {
        return true;
      }
    }
    return false;
  };

  /**
   * Compares base64 file content size with limit
   * @param {string} fileContent base64 file content
   * @param {number} sizeLimit file size limit in MB
   * @returns {boolean}
   */
  static checkBase64FileSize = (fileContent: string, sizeLimit: number | undefined) => {
    if (!sizeLimit) {
      return true;
    }
    const decodedBase64Data = atob(fileContent.replace(/^data:.*;base64,/, ""));
    const fileSizeInBytes = decodedBase64Data.length;

    return fileSizeInBytes / 1024 / 1024 <= sizeLimit;
  };

  public static getIsMobile = (): boolean => {
    return Boolean(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  public static getDefaultKuikaTableNames = () => {
    // Keep in sync with ManagedDbConstants.KuikaTables and
    // mcp-chat-server sensitive-table-creation-guard PLATFORM_RESERVED (K* block).
    const tables = [
      "KDevice",
      "KDeviceUserHistory",
      "KDomain",
      "KLog",
      "KNotification",
      "KNotificationTemplate",
      "KRefreshTokens",
      "KResetPasswordToken",
      "KResource",
      "KRole",
      "KUserNotification",
      "KUserPasswordHistory",
      "KUserRole",
      "KUserStripe",
      "KEmailConfirmation",
      "KUser",
      "KGpsTracking",
      "KQueue",
      "KWorkflowRegistry",
      "KMenu",
      "KMenuRole",
      "KApplication",
      "KApplicationRole",
      "KIyzicoActionResult",
      "KGeneratedAppActionAccess",
      "KNotificationLanguageTemplate",
      "KExtendedProperty",
      "KOfflineDatabase",
      "KParamActionResult",
      "KRoleGeneratedAppActionAccess",
      "KActionRoute",
      "KEntity",
      "KEntityAttributeMap",
      "KEntityAttributeValue",
      "KEntityValue",
      "KEnumItems",
      "KLocalization",
      "KAttribute",
      "KCategory",
      "KAccessDelegation",
      "KDelegatedAccessRights",
      "KDelegatedAccessRightRoleGroups",
      "KRoleGroup",
      "KRoleGroupChild",
      "KRoleGroupRole",
      "KWorkspaceDbMigration",
      "KTableConfig",
      "KAuditLog",
      "KAuditLogDetail",
      "KMobileAppSubscription",
      "KDocument",
      "KDocumentFolder",
      "KDocumentFolderName",
      "KDocumentVersion",
      "KDocumentPermission",
      "KDocumentShare",
      "KDocumentChunk",
      "KDocumentLock",
      "KDocumentAuditLog",
      "KDocumentRelation",
      "KDocumentTag",
      "KDocumentTemp",
      "KDocumentTemplate",
      "KDocumentTemplateChunk",
      "KDocumentUpload",
      "KDocumentUploadLink",
      "KDataScriptExecution"
    ];
    return tables;
  };

  public static getTypeNameBySqlDataType = (sqlDataType: string): BackEndValueType => {
    switch (sqlDataType) {
      case "boolean":
      case "bit":
        return "Boolean";

      case "datetime":
      case "datetime2":
      case "timestamp":
        return "Datetime";

      case "date":
        return "Date";

      case "datetimeoffset":
      case "timestamp with time zone":
        return "DateTimeOffset";

      case "decimal":
      case "numeric":
        return "Decimal";

      case "float":
      case "double precision":
        return "Double";

      case "uniqueidentifier":
      case "uuid":
        return "Guid";

      case "int":
      case "integer":
        return "Int32";

      case "smallint":
        return "Int16";

      case "bigint":
        return "Int64";

      case "nvarchar":
      case "varchar":
      case "character varying":
      case "text":
        return "String";

      case "bigint identity(1,1) not":
      case "INTEGER GENERATED ALWAYS AS IDENTIT".toLowerCase():
        return "AutoIncrement";

      case "varbinary":
      case "BYTEA".toLowerCase():
        return "Bytea";

      case "NUMBER(1)":
        return "Boolean";

      case "NUMBER(0)":
        return "Int64";

      case "TIMESTAMP":
        return "Datetime";

      case "Date":
        return "Date";

      case "NUMBER(18)":
        return "Decimal";

      case "CLOB":
        return "String";

      case "BINARY_DOUBLE":
        return "Double";
      case "RAW":
        return "Guid";
      case "NUMBER(10)":
        return "Int32";
      case "NUMBER(5)":
        return "Int16";
      case "NUMBER(19)":
        return "Int64";
      case "NVARCHAR2":
        return "String";
      // TIMESTAMP(6) WITH TIME ZONE
      case "TIMESTAMP(6) WITH TIME ZONE":
      case "timestamp with time zone":
        return "DateTimeOffset";
      default:
        return "Unknown";
    }
  };

  public static calculateGenericSelectParameters = (
    selectData: IGenericSelectDataModel | undefined
  ): IActionParameter[] => {
    const params: any[] = [];
    if (!selectData) return [];
    selectData.whereData.forEach((whereData) => {
      if (whereData.type === EManagedDbUpdateWhereType.and) {
        if (whereData.andCondition!.paramSymbol.category === ESymbolCategory.sqlParameter) {
          params.push({
            name: whereData.andCondition!.paramSymbol?.sqlParamName ?? whereData.andCondition?.colName,
            Typename: KMainFunctions.getTypeNameBySqlDataType(whereData.andCondition!.colSqlDataType)
          });
        }
      } else {
        whereData.orConditions?.forEach((orCondition) => {
          if (orCondition.paramSymbol.category === ESymbolCategory.sqlParameter) {
            params.push({
              name: orCondition.paramSymbol?.sqlParamName ?? orCondition.colName,
              Typename: KMainFunctions.getTypeNameBySqlDataType(orCondition.colSqlDataType)
            });
          }
        });
      }
    });

    return params;
  };

  public static defaultUniqueListElementNameGenerator = (
    list: any[],
    nameProperty: string,
    defaultNamePrefix: string
  ) => {
    const usedNames = new Set();

    list.forEach((item) => {
      if (item[nameProperty]) usedNames.add(item[nameProperty].toLowerCase());
    });

    let index = 1;
    let uniqueName = `${defaultNamePrefix}${index}`;

    // Culture-invariant fold: the set above is built with `toLowerCase()`
    // (locale-independent), so the lookup must use the same API. `toLocaleLowerCase()`
    // folds i/İ/ı differently under tr-TR, which would defeat the dedup check and
    // hand back a name that already exists on Turkish-locale machines.
    while (usedNames.has(uniqueName.toLowerCase())) {
      index++;
      uniqueName = `${defaultNamePrefix}${index}`;
    }

    return uniqueName;
  };

  public static getIsStringContainsNonLatinCharacter = (str: string): boolean => {
    return /[^\u0000-\u007F]+/.test(str);
  };

  public static getIsStringContainsSpecialCharacter = (str: string): boolean => {
    return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(str);
  };

  public static hasUserAccessInWorkspace = (assetName: string): boolean => {
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    if (!isDomainAuthorizationEnabled) return true;
    const hasAccess = window.userInfo?.actionAccesses?.filter((x) => x.code === assetName).length > 0;
    return hasAccess;
  };

  // While the user is on a protected branch they can't commit to, the whole designer behaves as
  // view-only (regardless of their real role) — the same effect as the Viewer role. Rather than a
  // bespoke canvas lock, we flip the single access gate below into "view/preview parts only", which
  // reuses every existing role-based edit/tab gate. Set from branch context (see branching-provider).
  private static branchReadOnly = false;

  public static setBranchReadOnly = (value: boolean): void => {
    KMainFunctions.branchReadOnly = value;
  };

  public static isBranchReadOnly = (): boolean => {
    return KMainFunctions.branchReadOnly;
  };

  private static isViewOnlyAccessPart = (assetName: string): boolean => {
    const code = (assetName || "").toLowerCase();
    return code.includes("view") || code.includes("preview");
  };

  /**
   * Collab viewer lock: while this tab is a read-only VIEWER of a live collaboration
   * session (another user holds the writer role), every edit affordance must be denied.
   * Same fail-closed pattern as branchReadOnly, but checked BEFORE the domain-auth early
   * return — a collab viewer is read-only regardless of the deployment's auth mode.
   */
  private static collabViewerReadOnly: boolean = false;

  public static setCollabViewerReadOnly = (value: boolean): void => {
    KMainFunctions.collabViewerReadOnly = value;
  };

  public static isCollabViewerReadOnly = (): boolean => {
    return KMainFunctions.collabViewerReadOnly;
  };

  public static hasUserAccessInWorkspaceByProjectRole = (assetName: string, accessList: IProjectAccess[]): boolean => {
    if (KMainFunctions.collabViewerReadOnly && !KMainFunctions.isViewOnlyAccessPart(assetName)) {
      return false;
    }
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    if (!isDomainAuthorizationEnabled) return true;
    // On a protected, non-committable branch: allow only view/preview capabilities; deny every
    // edit/manage part (fail-closed, so new write parts stay locked). The backend still enforces.
    if (KMainFunctions.branchReadOnly && !KMainFunctions.isViewOnlyAccessPart(assetName)) {
      return false;
    }
    const hasAccessInWorkspace = this.hasUserAccessInWorkspace(assetName);
    const hasAccessInProject = accessList?.filter((x) => x.code === assetName).length > 0;
    return hasAccessInWorkspace || hasAccessInProject;
  };

  public static isDomainAuthorizationEnabled = (): boolean => {
    return LocalStorageService.getDomainAuthInfo() === "true";
  };

  public static generateRandomString(length: number): string {
    // Used for security-sensitive values (e.g. OTP secret keys), so draw from a
    // cryptographically secure RNG rather than Math.random. Rejection sampling keeps
    // the character distribution uniform (no modulo bias).
    const characters: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength: number = characters.length;
    const maxUnbiased: number = 256 - (256 % charactersLength);
    let result: string = "";
    while (result.length < length) {
      const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(length - result.length);
      crypto.getRandomValues(bytes);
      for (let i = 0; i < bytes.length; i += 1) {
        const value: number = bytes[i];
        if (value < maxUnbiased) {
          result += characters.charAt(value % charactersLength);
        }
      }
    }
    return result;
  }

  public static getFormattedSystemActionName(actionDefinition: IActionDefinitionDto) {
    const systemActions = [
      EDatasourceType.SystemCSFunctions,
      EDatasourceType.SystemJSFunctions,
      EDatasourceType.ManagedDb,
      EDatasourceType.ManagedDbOracle,
      EDatasourceType.ManagedDbPostgre
    ];
    let name = actionDefinition.displayName ?? actionDefinition.name;

    if (
      !systemActions.includes(actionDefinition.actionType) ||
      ((actionDefinition.actionType === EDatasourceType.ManagedDb ||
        actionDefinition.actionType === EDatasourceType.ManagedDbPostgre ||
        actionDefinition.actionType === EDatasourceType.ManagedDbOracle) &&
        name !== "SaveRecord" &&
        name !== "DeleteRecord")
    ) {
      return name;
    }

    switch (name) {
      case "Divide":
        name = "Divide";
        break;
      case "Multiply":
        name = "Multiply";
        break;
      case "Subtract":
        name = "Subtract";
        break;
      case "Sum":
        name = "Sum";
        break;
      case "AddUserToRole":
        name = "Add User To Role";
        break;
      case "ChangeTwoFactorAuthenticationType":
        name = "Change Two Factor Authentication Type";
        break;
      case "CreateUser":
        name = "Create User";
        break;
      case "CreateUserWithPassword":
        name = "Create User With Password";
        break;
      case "DeleteMyAccount":
        name = "Delete My Account";
        break;
      case "DisableUser":
        name = "Disable User";
        break;
      case "EnableUser":
        name = "Enable User";
        break;
      case "ForgotPassword":
        name = "Forgot Password";
        break;
      case "GenerateAuthenticatorAppUri":
        name = "Generate Authenticator App Uri";
        break;
      case "GetAllUsers":
        name = "Get All Users";
        break;
      case "GetApplicationManagementAccessListByRoleId":
        name = "Get Application Management Access List By Role Id";
        break;
      case "GetAuthorizationManagementAccessListByRoleId":
        name = "Get Authorization Management Access List By Role Id";
        break;
      case "GetDeviceManagementAccessListByRoleId":
        name = "Get Device Management Access List By Role Id";
        break;
      case "GetMenuManagementAccessListByRoleId":
        name = "Get Menu Management Access List By Role Id";
        break;
      case "GetRoles":
        name = "Get Roles";
        break;
      case "GetSubscriptionManagementAccessListByRoleId":
        name = "Get Subscription Management Access List By Role Id";
        break;
      case "GetUserById":
        name = "Get User By Id";
        break;
      case "GetWorkflowManagementAccessListByRoleId":
        name = "Get Workflow Management Access List By Role Id";
        break;
      case "IsLoggedIn":
        name = "Is Logged In";
        break;
      case "IsVerificationCodeValid":
        name = "Is Verification Code Valid";
        break;
      case "Logout":
        name = "Logout";
        break;
      case "PasswordChange":
        name = "Password Change";
        break;
      case "PasswordReset":
        name = "Password Reset";
        break;
      case "RemoveUserFromRole":
        name = "Remove User From Role";
        break;
      case "SaveRoleAccess":
        name = "Save Role Access";
        break;
      case "SaveUser":
        name = "Save User";
        break;
      case "SendVerificationCode":
        name = "Send Verification Code";
        break;
      case "SetPasswordWithVerificationCode":
        name = "Set Password With Verification Code";
        break;
      case "Signin":
        name = "Sign In";
        break;
      case "SigninWithApple":
        name = "Sign In With Apple";
        break;
      case "SigninWithAzure":
        name = "Sign In With Azure";
        break;
      case "SigninWithGoogle":
        name = "Sign In With Google";
        break;
      case "SigninWithDelegation":
        name = "Sign In With Delegation";
        break;
      case "Signup":
        name = "Sign Up";
        break;
      case "UpdateUsername":
        name = "Update Username";
        break;
      case "UpdateUserStartingScreen":
        name = "Update User Starting Screen";
        break;
      case "IfThenElse":
        name = "If Then Else";
        break;
      case "ComponentToPdf":
        name = "Component To PDF";
        break;
      case "CopyToClipboard":
        name = "Copy To Clipboard";
        break;
      case "DownloadFile":
        name = "Download File";
        break;
      case "DownloadReport":
        name = "Download Report";
        break;
      case "GetComponentBase64":
        name = "Get Component Base64";
        break;
      case "GetReportBase64":
        name = "Get Report Base64";
        break;
      case "GetMultipleReportsBase64":
        name = "Get Multiple Reports Base64";
        break;
      case "GetVersionInfo":
        name = "Get Version Info";
        break;
      case "GetWifiNetworkInfo":
        name = "Get Wifi Network Info";
        break;
      case "GoToFullScreen":
        name = "Go To Full Screen";
        break;
      case "IsAndroid":
        name = "Is Android";
        break;
      case "IsIOS":
        name = "Is iOS";
        break;
      case "IsOnline":
        name = "Is Online";
        break;
      case "IsWeb":
        name = "Is Web";
        break;
      case "OpenInBrowser":
        name = "Open In Browser";
        break;
      case "OpenInMap":
        name = "Open In Map";
        break;
      case "OpenInWebView":
        name = "Open In Web View";
        break;
      case "OpenPdf":
        name = "Open PDF";
        break;
      case "PhoneNumberCall":
        name = "Phone Number Call";
        break;
      case "PhotoFromGallery":
        name = "Photo From Gallery";
        break;
      case "PhotoTake":
        name = "Photo Take";
        break;
      case "PrintPdf":
        name = "Print PDF";
        break;
      case "ReadNFC":
        name = "Read NFC";
        break;
      case "ScanBarcode":
        name = "Scan Barcode";
        break;
      case "ScanPharmaCode":
        name = "Scan Pharma Code";
        break;
      case "ScrollTo":
        name = "Scroll To";
        break;
      case "SendWhatsappMessage":
        name = "Send Whatsapp Message";
        break;
      case "Share":
        name = "Share";
        break;
      case "UploadFile":
        name = "Upload File";
        break;
      case "DownloadVcf":
        name = "Download VCF";
        break;
      case "ExportExcel":
        name = "Export Excel";
        break;
      case "GenerateQrCode":
        name = "Generate QR Code";
        break;
      case "GenerateVcf":
        name = "Generate VCF";
        break;
      case "GetExcelBase64":
        name = "Get Excel Base64";
        break;
      case "ImportExcelBase64":
        name = "Import Excel Base64";
        break;
      case "LocationAddressOfCoordinates":
        name = "Location Address Of Coordinates";
        break;
      case "LocationCoordinatesOfAddress":
        name = "Location Coordinates Of Address";
        break;
      case "LocationGetCurrentCoordinates":
        name = "Location Get Current Coordinates";
        break;
      case "DeleteFromLocalStorage":
        name = "Delete From Local Storage";
        break;
      case "DeleteFromSessionStorage":
        name = "Delete From Session Storage";
        break;
      case "ReadFromLocalStorage":
        name = "Read From Local Storage";
        break;
      case "ReadFromSessionStorage":
        name = "Read From Session Storage";
        break;
      case "WriteToLocalStorage":
        name = "Write To Local Storage";
        break;
      case "WriteToSessionStorage":
        name = "Write To Session Storage";
        break;
      case "SaveRecord":
        name = "Save Record";
        break;
      case "DeleteRecord":
        name = "Delete Record";
        break;
      case "GetLanguage":
        name = "Get Language";
        break;
      case "SelectLanguage":
        name = "Select Language";
        break;
      case "SetLanguage":
        name = "Set Language";
        break;
      case "GenerateLink":
        name = "Generate Link";
        break;
      case "GetAllMenuItems":
        name = "Get All Menu Items";
        break;
      case "GetAuthorizedMenuItems":
        name = "Get Authorized Menu Items";
        break;
      case "GoBack":
        name = "Go Back";
        break;
      case "NAVIGATE":
        name = "Navigate";
        break;
      case "GetUsersNotification":
        name = "Get Users Notification";
        break;
      case "MarkAsReadNotification":
        name = "Mark As Read Notification";
        break;
      case "Notify":
        name = "Notify";
        break;
      case "SendMail":
        name = "Send Mail";
        break;
      case "SendPushNotification":
        name = "Send Push Notification";
        break;
      case "ActivateSubscription":
        name = "Activate Subscription";
        break;
      case "CancelSubscription":
        name = "Cancel Subscription";
        break;
      case "InitializeCheckoutForm":
        name = "Initialize Checkout Form";
        break;
      case "IsSubscriptionAvailable":
        name = "Is Subscription Available";
        break;
      case "RetrieveSubscription":
        name = "Retrieve Subscription";
        break;
      case "RetrySubscription":
        name = "Retry Subscription";
        break;
      case "UpgradeSubscription":
        name = "Upgrade Subscription";
        break;
      case "CancelStripePayment":
        name = "Cancel Stripe Payment";
        break;
      case "CaptureReservedFunds":
        name = "Capture Reserved Funds";
        break;
      case "ChargePaymentMethod":
        name = "Charge Payment Method";
        break;
      case "CheckPaymentIntentStatus":
        name = "Check Payment Intent Status";
        break;
      case "ConnectedAccountPayout":
        name = "Connected Account Payout";
        break;
      case "ConnectedAccountWithdraw":
        name = "Connected Account Withdraw";
        break;
      case "CreateConnectedAccount":
        name = "Create Connected Account";
        break;
      case "GenerateStripeOnboardingLink":
        name = "Generate Stripe Onboarding Link";
        break;
      case "GetCustomerPaymentMethods":
        name = "Get Customer Payment Methods";
        break;
      case "GetCustomerPaymentMethodsById":
        name = "Get Customer Payment Methods By Id";
        break;
      case "GetPaymentReceiptLink":
        name = "Get Payment Receipt Link";
        break;
      case "OpenStripePaymentDrawer":
        name = "Open Stripe Payment Drawer";
        break;
      case "PaymentIntentStatus":
        name = "Payment Intent Status";
        break;
      case "RefundStatus":
        name = "Refund Status";
        break;
      case "RefundStripePayment":
        name = "Refund Stripe Payment";
        break;
      case "RemovePaymentMethod":
        name = "Remove Payment Method";
        break;
      case "SetupIntent":
        name = "Setup Intent";
        break;
      case "StripeConnectedAccountOnboardingStatus":
        name = "Stripe Connected Account Onboarding Status";
        break;
      case "UpdateStripeUserInfo":
        name = "Update Stripe User Info";
        break;
      case "GetApplications":
        name = "Get Applications";
        break;
      case "GetProcessData":
        name = "Get Process Data";
        break;
      case "GetProcessInstance":
        name = "Get Process Instance";
        break;
      case "GetProcessInstances":
        name = "Get Process Instances";
        break;
      case "GetProcessStatusOptions":
        name = "Get Process Status Options";
        break;
      case "GetProcessStep":
        name = "Get Process Step";
        break;
      case "SuspendProcessInstance":
        name = "Suspend Process Instance";
        break;
      case "RetryProcessInstance":
        name = "Retry Process Instance";
        break;
      case "TerminateProcessInstance":
        name = "Terminate Process Instance";
        break;
      case "AssignTaskToUser":
        name = "Assign Task To User";
        break;
      case "ClaimTask":
        name = "Claim Task";
        break;
      case "FindManager":
        name = "Find Manager";
        break;
      case "GetAgent":
        name = "Get Agent";
        break;
      case "GetMyTasks":
        name = "Get My Tasks";
        break;
      case "GetMyTasksInfo":
        name = "Get My Tasks Info";
        break;
      case "GetProcessHappyPathSummary":
        name = "Get Process Happy Path Summary";
        break;
      case "GetProcessesHistory":
        name = "Get Processes History";
        break;
      case "GetProcessesStartedByMe":
        name = "Get Processes Started By Me";
        break;
      case "GetTaskDetail":
        name = "Get Task Detail";
        break;
      case "GetUserTaskCount":
        name = "Get User Task Count";
        break;
      case "GetUserTaskSummary":
        name = "Get User Task Summary";
        break;
      case "NavigateToTask":
        name = "Navigate To Task";
        break;
      case "ReleaseTaskToPool":
        name = "Release Task To Pool";
        break;
      case "ResumeProcess":
        name = "Resume Process";
        break;
      case "SetAgent":
        name = "Set Agent";
        break;
      case "SetManager":
        name = "Set Manager";
        break;
      case "StartProcess":
        name = "Start Process";
        break;
      case "RunUIVisionMacro":
        name = "Run UI.Vision Macro";
        break;
      case "Substring":
        name = "Substring";
        break;
      case "Subarray":
        name = "Subarray";
        break;
      case "Concatenate":
        name = "Concatenate";
        break;
      case "Trigger":
        name = "Trigger";
        break;
      case "ChangeEnabledOf":
        name = "Change Enabled Of";
        break;
      case "ChangeVisibilityOf":
        name = "Change Visibility Of";
        break;
      case "SetFocusTo":
        name = "Set Focus To";
        break;
      case "SetValueOf":
        name = "Set Value Of";
        break;
      case "ToggleMenuCollapse":
        name = "Toggle Menu Collapse";
        break;
      case "ToggleHamburgerMenuCollapse":
        name = "Toggle Hamburger Menu Collapse";
        break;
      case "ToggleVisibilityOf":
        name = "Toggle Visibility Of";
        break;
      case "OpenCollapsePanelOf":
        name = "Open Collapse Panel Of";
        break;
      case "CloseCollapsePanelOf":
        name = "Close Collapse Panel Of";
        break;
      case "ToggleCollapsePanelOf":
        name = "Toggle Collapse Panel Of";
        break;
      case "StartExternalProcess":
        name = "Start External Process";
        break;
      case "MigrateProcessInstances":
        name = "Migrate Process Instances";
        break;
      case "AnalyzeProcessMigration":
        name = "Analyze Process Migration";
        break;
      default:
        name = this.convertPascalCaseToText(name);
        break;
    }

    return `${actionDefinition.isDeprecated ? "(Deprecated) " : ""}${name}`;
  }

  public static convertPascalCaseToText(input?: string) {
    if (!input) return "";

    return input
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // Insert space between uppercase letter sequence and single uppercase letter followed by lowercase
      .replace(/([a-z\d])([A-Z])/g, "$1 $2") // Insert space between lowercase/digit and uppercase letter
      .trim();
  }

  public static generateRandomHEXColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  public static findParameter(parameters: IActionParameter[], parameterName: string): IActionParameter | undefined {
    let param = parameters.find((p) => p.name === parameterName);
    if (param) return param;

    for (const prm of parameters) {
      if (prm.parameters && prm.parameters.length > 0) {
        param = this.findParameter(prm.parameters, parameterName);
        if (param) {
          break;
        }
      }
    }
    return param;
  }

  /**
   * Clicks the button element on "feedback-modal.tsx:472"
   */
  public static openFeedbackModal = () => {
    const feedbackBtn = document.getElementById("kuika-feedback-btn");

    if (feedbackBtn) feedbackBtn.click();
    else {
      setTimeout(() => {
        this.openFeedbackModal();
      }, 200);
    }
  };

  public static getImageUrlByIconName = (iconName: string | undefined): string => {
    // One folder, one asset. The glyphs are single-colour masks now, so theme and hover are the
    // stylesheet's business (--kuika-svg-color) and no longer pick a file.
    const base: string = "/images/componentIcons/";

    if (iconName === undefined) return `${base}default-icon-32.svg`;
    if (iconName === "Auto Complate" || iconName === "AutoComplete") return `${base}auto-complate-32.svg`;
    if (iconName === "Badge") return `${base}badge-32.svg`;
    if (iconName === "Barcode Renderer" || iconName === "BarcodeRenderer") return `${base}barcode-renderer-32.svg`;
    if (iconName === "Breadcrumb") return `${base}breadcrumb-32.svg`;
    if (iconName === "Button") return `${base}button-32.svg`;
    if (iconName === "Calendar") return `${base}calendar-32.svg`;
    if (iconName === "Chart Area" || iconName === "AreaChart") return `${base}chart-area-32.svg`;
    if (iconName === "Chart Bar" || iconName === "BarChart") return `${base}chart-bar-32.svg`;
    if (iconName === "Chart Donut" || iconName === "DonutChart") return `${base}chart-donut-32.svg`;
    if (iconName === "Chart Gauge" || iconName === "GaugeChart") return `${base}chart-gauge-32.svg`;
    if (iconName === "Chart Line" || iconName === "LineChart") return `${base}chart-line-32.svg`;
    if (iconName === "Chart Pie" || iconName === "PieChart") return `${base}chart-pie-32.svg`;
    if (iconName === "Chart Mixed" || iconName === "MixedChart") return `${base}chart-mixed-32.svg`;
    if (iconName === "Chart Radar" || iconName === "RadarChart") return `${base}chart-radar-32.svg`;
    if (iconName === "Chart Radial" || iconName === "RadialChart") return `${base}chart-radial-32.svg`;
    if (iconName === "Chat") return `${base}chat-32.svg`;
    if (iconName === "Checkbox") return `${base}checkbox-32.svg`;
    if (iconName === "Column" || iconName === "Col") return `${base}column-32.svg`;
    if (iconName === "Color Picker" || iconName === "ColorPicker") return `${base}color-picker-32.svg`;
    if (iconName === "Collapse") return `${base}collapse-32.svg`;
    if (iconName === "Splitter") return `${base}splitter-32.svg`;
    if (iconName === "ContentMenu" || iconName === "Content Menu") return `${base}content-menu-32.svg`;
    if (iconName === "ContentMenuPanel") return `${base}content-menu-panel-32.svg`;
    if (iconName === "ContentMenuItem") return `${base}content-menu-item-32.svg`;
    if (iconName === "Countdown") return `${base}countdown-32.svg`;
    if (iconName === "Countup") return `${base}countup-32.svg`;
    if (iconName === "Currency") return `${base}currency-32.svg`;
    if (iconName === "Date Range" || iconName === "DateRange") return `${base}date-range-32.svg`;
    if (iconName === "Date Time Range" || iconName === "DateTimeRange") return `${base}date-time-range-32.svg`;
    if (iconName === "Default Icon") return `${base}default-icon-32.svg`;
    if (iconName === "Divider") return `${base}divider-32.svg`;
    if (iconName === "Email") return `${base}email-32.svg`;
    if (iconName === "Float Button" || iconName === "FloatButton") return `${base}float-button-32.svg`;
    if (iconName === "Footer") return `${base}footer-32.svg`;
    if (iconName === "Page Footer") return `${base}footer-32.svg`;
    if (iconName === "Form") return `${base}form-32.svg`;
    if (iconName === "Gallery" || iconName === "GalleryView") return `${base}gallery-32.svg`;
    if (iconName === "Grid" || iconName === "FlexGrid") return `${base}grid-32.svg`;
    if (iconName === "Header") return `${base}header-32.svg`;
    if (iconName === "Page Header") return `${base}header-32.svg`;
    if (iconName === "Horizontal Stack" || iconName === "HorizontalStack") return `${base}horizontal-stack-32.svg`;
    if (iconName === "Horizontal Grid" || iconName === "HorizontalGrid") return `${base}horizontal-grid-32.svg`;
    if (iconName === "Icon") return `${base}icon-32.svg`;
    if (iconName === "Image" || iconName === "Thumbnail") return `${base}image-32.svg`;
    if (iconName === "Image Carousel" || iconName === "Carousel") return `${base}image-carousel-32.svg`;
    if (iconName === "Input Numeric" || iconName === "NumberInput" || iconName === "BarcodeInput")
      return `${base}input-numeric-32.svg`;
    if (iconName === "Kanban") return `${base}kanban-32.svg`;
    if (iconName === "Label") return `${base}label-32.svg`;
    if (iconName === "Language Selector") return `${base}language-selector-32.svg`;
    if (iconName === "Location Search" || iconName === "LocationSearch") return `${base}location-search-32.svg`;
    if (iconName === "Map Location View" || iconName === "Maps") return `${base}map-location-view-32.svg`;
    if (iconName === "Location Picker" || iconName === "LocationPicker") return `${base}location-picker-32.svg`;
    if (iconName === "Marquee") return `${base}marquee-32.svg`;
    if (iconName === "Menu") return `${base}menu-32.svg`;
    if (iconName === "MDEditor") return `${base}mdeditor-32.svg`;
    if (iconName === "MDViewer") return `${base}mdviewer-32.svg`;
    if (iconName === "Micro App" || iconName === "MicroApp") return `${base}micro-app-32.svg`;
    if (iconName === "Multi Select" || iconName === "MultiSelect") return `${base}multi-select-32.svg`;
    if (iconName === "Month Picker" || iconName === "MonthPicker") return `${base}month-picker-32.svg`;
    if (iconName === "Node Tree" || iconName === "NodeTree") return `${base}node-tree-32.svg`;
    if (iconName === "Pagination") return `${base}pagination-32.svg`;
    if (iconName === "Panel") return `${base}panel-32.svg`;
    if (iconName === "Password") return `${base}password-32.svg`;
    if (iconName === "Percent") return `${base}percent-32.svg`;
    if (iconName === "Picker Date" || iconName === "Date") return `${base}picker-date-32.svg`;
    if (iconName === "Picker Date Time" || iconName === "DateTime") return `${base}picker-date-time-32.svg`;
    if (iconName === "Picker Location" || iconName === "LocationPicker") return `${base}picker-location-32.svg`;
    if (iconName === "Picker Time") return `${base}picker-time-32.svg`;
    if (iconName === "Process Automation") return `${base}process-automation-32.svg`;
    if (iconName === "PA Interaction Buttons") return `${base}pa-interaction-buttons-32.svg`;
    if (iconName === "Progress Bar" || iconName === "ProgressBar") return `${base}progress-bar-32.svg`;
    if (iconName === "Progress Circle" || iconName === "ProgressCircle") return `${base}progress-circle-32.svg`;
    if (iconName === "QR Renderer" || iconName === "QRRenderer") return `${base}qr-renderer-32.svg`;
    if (iconName === "Radio Group" || iconName === "RadioGroup" || iconName === "RadioButtonGroup")
      return `${base}radio-group-32.svg`;
    if (iconName === "Range" || iconName === "Range Slider") return `${base}range-32.svg`;
    if (iconName === "Rate") return `${base}rate-32.svg`;
    if (iconName === "Row") return `${base}row-32.svg`;
    if (iconName === "Screen" || iconName === "div") return `${base}screen-32.svg`;
    if (iconName === "Select" || iconName === "SelectBox") return `${base}select-32.svg`;
    if (iconName === "Signature") return `${base}signature-32.svg`;
    if (iconName === "Slider") return `${base}slider-32.svg`;
    if (iconName === "Stepper") return `${base}stepper-32.svg`;
    if (iconName === "Stopwatch") return `${base}stopwatch-32.svg`;
    if (iconName === "Table" || iconName === "Pivot Table" || iconName === "ReportTable" || iconName === "StaticTable")
      return `${base}table-32.svg`;
    if (iconName === "TableHeader" || iconName === "ReportTableHeader" || iconName === "StaticTableHeader")
      return `${base}table-header-32.svg`;
    if (iconName === "TableRow" || iconName === "ReportTableRow" || iconName === "StaticTableRow")
      return `${base}table-row-32.svg`;
    if (iconName === "TableTemplateColumn" || iconName === "StaticTableColumn" || iconName === "ReportTableColumn")
      return `${base}table-column-32.svg`;
    if (iconName === "TableDefaultColumn") return `${base}table-column-32.svg`;
    if (iconName === "Tabs" || iconName === "Tab") return `${base}tabs-32.svg`;
    if (iconName === "Text Area" || iconName === "TextArea") return `${base}text-area-32.svg`;
    if (iconName === "Text Input" || iconName === "TextInput") return `${base}text-input-32.svg`;
    if (iconName === "Tree") return `${base}tree-32.svg`;
    if (iconName === "Timeline") return `${base}timeline-32.svg`;
    if (iconName === "Toggle Switch" || iconName === "Switch") return `${base}toggle-switch-32.svg`;
    if (iconName === "Tooltip") return `${base}tooltip-32.svg`;
    if (iconName === "Uploader" || iconName === "BigFileUpload") return `${base}uploader-32.svg`;
    if (iconName === "Url") return `${base}url-32.svg`;
    if (iconName === "Vertical Stack" || iconName === "VerticalStack") return `${base}vertical-stack-32.svg`;
    if (iconName === "Video Player" || iconName === "VideoPlayer") return `${base}video-player-32.svg`;
    if (iconName === "Year Picker" || iconName === "YearPicker") return `${base}year-picker-32.svg`;
    if (iconName === "Web View" || iconName === "WebView") return `${base}webview-32.svg`;
    if (iconName === "Recaptcha") return `${base}recaptcha.svg`;
    if (iconName === "File Directory") return `${base}file-directory.svg`;
    if (iconName === "Document Uploader") return `${base}document-uploader.svg`;
    if (iconName === "Document Editor") return `${base}document-editor.svg`;

    return `${base}default-icon-32.svg`;
  };

  /**
   * A placed component's icon. The declared `icon` is the key the Elements panel draws with, so
   * it goes first; the definition name covers the definitions whose declared icon has no asset
   * (table and content-menu parts). Never pass the display item's own `name` here: saved screens
   * and screen templates store it in any casing ("row", "verticalstack"), and the lookup is exact.
   */
  public static getImageUrlByComponentDefinition = (definition: IComponentDefinition | undefined): string => {
    const defaultUrl: string = KMainFunctions.getImageUrlByIconName(undefined);
    for (const key of [definition?.icon, definition?.name]) {
      const url: string = KMainFunctions.getImageUrlByIconName(key);
      if (url !== defaultUrl) return url;
    }
    return defaultUrl;
  };

  public static getDefaultSymbol = (typename: BackEndValueType): ISymbol => {
    let fixedValue: any;
    if (typename === "Boolean") {
      fixedValue = false;
    }
    if (
      typename === "Decimal" ||
      typename === "Double" ||
      typename === "Int16" ||
      typename === "Int32" ||
      typename === "Int64" ||
      typename === "Single"
    ) {
      fixedValue = 0;
    }
    if (typename === "String") {
      fixedValue = "";
    }
    const symbol: ISymbol = {
      category: ESymbolCategory.fixed,
      isFixed: true,
      label: "Fixed Value",
      fixedValue,
      description: ""
    };
    return symbol;
  };

  static constructGeneratedFrontendUrl = (sd: IScreenDesignContextState): string => {
    const platformDomain = isMainAI ? "havelsan.com.tr" : "kuika.io";
    const environmentName = window.location.hostname.split(".")[0].toLocaleLowerCase().replace(/_/g, "-");
    const projectName = sd.project?.name?.toLocaleLowerCase().replace(/_/g, "-");
    const domainName = window.currentDomain?.toLocaleLowerCase().replace(/_/g, "-");

    const generatedAppUrl: string =
      `https://${domainName.toLowerCase()}.${environmentName.toLowerCase()}.${platformDomain}/${projectName}`.replace(
        /_/g,
        "-"
      );
    // Canonical trailing-slash form — the slash-less URL 301s to a
    // scheme-downgraded http URL that Safari cannot open (KOA-17913).
    return normalizePreviewUrl(generatedAppUrl);
  };

  public static convertRgbaToHex = (rgba: string) => {
    if (!rgba) return "";
    if (rgba.startsWith("#")) return rgba;

    const matches = rgba.match(/\d+/g);
    if (!matches || matches.length < 3) return "";

    const r = parseInt(matches[0]).toString(16).padStart(2, "0")?.toUpperCase();
    const g = parseInt(matches[1]).toString(16).padStart(2, "0")?.toUpperCase();
    const b = parseInt(matches[2]).toString(16).padStart(2, "0")?.toUpperCase();

    return `${r}${g}${b}`;
  };

  public static isMainAI = () => isMainAI;

  public static getOlderGeminiVersions = () => ["gemini-1.5-flash-001"];

  public static getLastGeminiVersion = () => "gemini-2.5-flash";

  public static getFileUploadModels = () => [
    "gemini-2.5-flash",
    "gemini-2.5-pro",
    "gpt-5",
    "gpt-5-mini",
    "gpt-5.1",
    "gpt-5.2",
    "gpt-5.4",
    "gpt-5.4-mini"
  ];
}
